import { ChangeDetectionStrategy, Component, ElementRef, HostListener, effect, signal, viewChild } from '@angular/core';

import { DxcIconComponent } from '../icon/dxc-icon.component';
import { DxcTourService } from './dxc-tour.service';

type Box = Record<string, string>;

/**
 * Anfitrión del recorrido de novedades. Se monta una vez, junto a
 * `<dxc-dialog-host>`, y pinta lo que diga `DxcTourService`:
 *
 * - la portada (`mode === 'intro'`): un diálogo partido con la escena animada
 *   que resume el cambio, y la salida de siempre —cerrar arriba, «Ahora no»—;
 * - el recorrido (`mode === 'steps'`): un foco sobre el elemento y una ficha
 *   junto a él. La ficha se recoloca al desplazar o cambiar el tamaño, porque
 *   el elemento vive en la página y no en una capa propia.
 *
 * Esc cierra en los dos modos, y cerrar cuenta como visto.
 */
@Component({
    standalone: true,
    selector: 'dxc-tour-host',
    imports: [DxcIconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (tour.mode() === 'intro' && tour.tour(); as t) {
            <div class="dxc-scrim dxc-scrim--fixed is-open" (click)="onScrim($event)">
                <section class="dxc-dialog dxc-dialog--split dxc-tour-intro is-open"
                    role="dialog" aria-modal="true" aria-labelledby="dxc-tour-title">
                    <aside class="dxc-dialog__aside dxc-tour-intro__aside">
                        @if (t.scene === 'load-details') {
                            <!-- La escena es el resumen en movimiento: la vista larga de
                                 una columna se reparte en dos, aparece la barra de
                                 secciones y, al pie de la derecha, el resumen. Con
                                 movimiento reducido se queda quieta en el estado final. -->
                            <div class="dxc-tour-scene" role="img"
                                aria-label="La vista pasa de una columna larga a dos columnas con una barra de secciones y un resumen a la derecha">
                                <span class="dxc-tour-scene__block dxc-tour-scene__head">Carga</span>
                                <span class="dxc-tour-scene__block dxc-tour-scene__nav"></span>
                                <span class="dxc-tour-scene__block dxc-tour-scene__cp">Carta Porte</span>
                                <span class="dxc-tour-scene__block dxc-tour-scene__rate">Tarifa</span>
                                <span class="dxc-tour-scene__block dxc-tour-scene__bids">Ofertas</span>
                                <span class="dxc-tour-scene__block dxc-tour-scene__driver">Operador</span>
                                <span class="dxc-tour-scene__block dxc-tour-scene__ship">Embarque</span>
                                <span class="dxc-tour-scene__block dxc-tour-scene__digest">Resumen</span>
                                <span class="dxc-tour-scene__caption dxc-tour-scene__caption--1">Antes: una columna muy larga</span>
                                <span class="dxc-tour-scene__caption dxc-tour-scene__caption--2">Tarifa y Ofertas, a la derecha</span>
                                <span class="dxc-tour-scene__caption dxc-tour-scene__caption--3">Una barra para saltar de sección</span>
                                <span class="dxc-tour-scene__caption dxc-tour-scene__caption--4">Un resumen que baja contigo</span>
                            </div>
                        }
                        <p class="dxc-dialog__aside-eyebrow">{{ t.eyebrow }}</p>
                    </aside>

                    <div class="dxc-dialog__pane">
                        <header class="dxc-dialog__head">
                            <button type="button" class="dxc-dialog__close" aria-label="Cerrar" (click)="tour.close()">
                                <dxc-icon name="close" [size]="18"></dxc-icon>
                            </button>
                            <h4 id="dxc-tour-title">{{ t.title }}</h4>
                            <p>{{ t.lead }}</p>
                        </header>
                        <div class="dxc-dialog__body">
                            <ul class="dxc-tour-intro__list">
                                @for (item of t.bullets; track item) { <li>{{ item }}</li> }
                            </ul>
                            <p class="dxc-muted dxc-tour-intro__hint">
                                Lo puedes volver a ver desde el botón de ayuda de la barra superior.
                            </p>
                        </div>
                        <footer class="dxc-dialog__foot">
                            <button type="button" class="dxc-btn dxc-btn--neutral" (click)="tour.close()">Ahora no</button>
                            <button type="button" class="dxc-btn dxc-btn--primary" (click)="tour.start()">
                                Ver recorrido · {{ tour.availableSteps().length }} pasos
                            </button>
                        </footer>
                    </div>
                </section>
            </div>
        }

        @if (tour.mode() === 'steps' && tour.step(); as step) {
            <div class="dxc-tour__spot" [style]="spot()" aria-hidden="true"></div>
            <section #cardEl class="dxc-tour__card" [style]="card()" tabindex="-1"
                role="dialog" aria-modal="false" aria-labelledby="dxc-tour-step-title">
                <span class="dxc-tour__count">Paso {{ tour.index() + 1 }} de {{ tour.steps().length }}</span>
                <h4 id="dxc-tour-step-title">{{ step.title }}</h4>
                <p>{{ step.body }}</p>
                <div class="dxc-tour__actions">
                    <button type="button" class="dxc-btn dxc-btn--ghost dxc-btn--sm" (click)="tour.close()">Saltar recorrido</button>
                    @if (tour.index() > 0) {
                        <button type="button" class="dxc-btn dxc-btn--neutral dxc-btn--sm" (click)="tour.prev()">Atrás</button>
                    }
                    <button type="button" class="dxc-btn dxc-btn--primary dxc-btn--sm" (click)="tour.next()">
                        {{ tour.index() === tour.steps().length - 1 ? 'Terminar' : 'Siguiente' }}
                    </button>
                </div>
            </section>
        }
    `,
})
export class DxcTourHostComponent {

    readonly spot = signal<Box>({});
    readonly card = signal<Box>({});
    readonly cardRef = viewChild<ElementRef<HTMLElement>>('cardEl');

    #frame = 0;

    constructor(public tour: DxcTourService) {
        // Cada paso nuevo: llevar el elemento a la vista, colocar foco y ficha, y
        // pasar el foco del teclado a la ficha para que el lector la anuncie.
        effect(() => {
            const step = this.tour.step();
            if (this.tour.mode() !== 'steps' || !step) return;
            const target = document.querySelector<HTMLElement>(step.target);
            if (!target) return;
            const tall = target.getBoundingClientRect().height > window.innerHeight * 0.6;
            target.scrollIntoView({ behavior: 'smooth', block: tall ? 'start' : 'center' });
            this.place();
            // La ficha no tiene alto hasta pintarse: se recoloca ya medida.
            setTimeout(() => {
                this.place();
                this.cardRef()?.nativeElement.focus({ preventScroll: true });
            });
        });
    }

    @HostListener('window:scroll')
    @HostListener('window:resize')
    onViewportChange(): void {
        if (this.tour.mode() !== 'steps' || this.#frame) return;
        this.#frame = requestAnimationFrame(() => {
            this.#frame = 0;
            this.place();
        });
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.tour.mode()) this.tour.close();
    }

    onScrim(event: MouseEvent): void {
        if (event.target === event.currentTarget) this.tour.close();
    }

    /**
     * Foco y ficha en coordenadas de ventana. La ficha va debajo del elemento si
     * cabe, encima si no, y si el elemento ocupa más que la ventana —una columna
     * entera—, a su lado o, en último caso, abajo dentro de la ventana.
     */
    private place(): void {
        const step = this.tour.step();
        const target = step && document.querySelector<HTMLElement>(step.target);
        if (!target) return;

        const pad = 6;
        const gap = 14;
        const margin = 16;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const r = target.getBoundingClientRect();
        const top = Math.max(r.top - pad, -pad);
        const bottom = Math.min(r.bottom + pad, vh + pad);

        this.spot.set({
            left: `${Math.round(r.left - pad)}px`,
            top: `${Math.round(top)}px`,
            width: `${Math.round(r.width + pad * 2)}px`,
            height: `${Math.max(0, Math.round(bottom - top))}px`,
        });

        const w = Math.min(360, vw - margin * 2);
        const h = this.cardRef()?.nativeElement.offsetHeight || 200;
        const clampX = (x: number) => Math.min(Math.max(x, margin), vw - w - margin);
        const clampY = (y: number) => Math.min(Math.max(y, margin), vh - h - margin);

        let x: number;
        let y: number;
        if (vh - bottom >= h + gap + margin) {
            x = clampX(r.left); y = bottom + gap;
        } else if (top >= h + gap + margin) {
            x = clampX(r.left); y = top - gap - h;
        } else if (r.left >= w + gap + margin) {
            x = r.left - gap - w; y = clampY(top + 24);
        } else if (vw - r.right >= w + gap + margin) {
            x = r.right + gap; y = clampY(top + 24);
        } else {
            x = clampX(r.left + 24); y = vh - h - margin;
        }

        this.card.set({ left: `${Math.round(x)}px`, top: `${Math.round(y)}px`, width: `${w}px` });
    }
}
